import { useState, useEffect } from 'react';
import axios from 'axios';
import { useSnackbar } from 'notistack';
import TextField from '@mui/material/TextField';
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';
import MetaData from '../Layouts/MetaData';
import BackdropLoader from '../Layouts/BackdropLoader';

const WalletSettings = () => {
    const { enqueueSnackbar } = useSnackbar();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [settings, setSettings] = useState({});

    useEffect(() => {
        const fetchSettings = async () => {
            try {
                const { data } = await axios.get('/api/v1/wallet-settings');
                setSettings(data.settings);
            } catch (error) {
                enqueueSnackbar(error?.response?.data?.message || "Failed to load wallet settings", { variant: "error" });
            } finally {
                setLoading(false);
            }
        };
        fetchSettings();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleChange = (key, value) => {
        setSettings((prev) => ({ ...prev, [key]: value }));
    };

    const saveHandler = async () => {
        setSaving(true);
        try {
            const { data } = await axios.put('/api/v1/admin/wallet-settings', settings);
            setSettings(data.settings);
            enqueueSnackbar("Wallet settings updated", { variant: "success" });
        } catch (error) {
            enqueueSnackbar(error?.response?.data?.message || "Failed to update settings", { variant: "error" });
        } finally {
            setSaving(false);
        }
    };

    if (loading) return <BackdropLoader />;

    return (
        <>
            <MetaData title="Admin: 1313 Wallet Settings | 1313 Mart" />

            <h1 className="text-lg font-medium uppercase mb-4">1313 Wallet Settings</h1>

            <div className="bg-white rounded-xl shadow-lg w-full p-6 flex flex-col gap-5 max-w-2xl">
                <FormControlLabel
                    control={<Switch checked={!!settings.enabled} onChange={(e) => handleChange('enabled', e.target.checked)} />}
                    label="Enable 1313 Wallet (points + cash) system-wide"
                />

                <div>
                    <p className="text-sm font-medium text-gray-700">Points earned per HK$1 spent</p>
                    <p className="text-xs text-gray-400 mb-2">Default 1 — a HK$500 order earns 500 points.</p>
                    <TextField
                        type="number"
                        size="small"
                        value={settings.pointsPerCurrencyEarned ?? ''}
                        onChange={(e) => handleChange('pointsPerCurrencyEarned', Number(e.target.value))}
                        sx={{ width: 200 }}
                    />
                </div>

                <div>
                    <p className="text-sm font-medium text-gray-700">Points needed to redeem HK$1</p>
                    <p className="text-xs text-gray-400 mb-2">Default 100 — 100 points = HK$1 off an order.</p>
                    <TextField
                        type="number"
                        size="small"
                        value={settings.pointsToCurrencyRedemption ?? ''}
                        onChange={(e) => handleChange('pointsToCurrencyRedemption', Number(e.target.value))}
                        sx={{ width: 200 }}
                    />
                </div>

                <div>
                    <p className="text-sm font-medium text-gray-700">Max % of an order payable with points</p>
                    <p className="text-xs text-gray-400 mb-2">Keeps every order tied to a real payment method. Set to 100 for no cap.</p>
                    <TextField
                        type="number"
                        size="small"
                        value={settings.maxRedemptionPercent ?? ''}
                        onChange={(e) => handleChange('maxRedemptionPercent', Number(e.target.value))}
                        sx={{ width: 200 }}
                        inputProps={{ min: 0, max: 100 }}
                    />
                </div>

                <div>
                    <p className="text-sm font-medium text-gray-700">1313 Cash top-up multiple (HK$)</p>
                    <p className="text-xs text-gray-400 mb-2">Customers can only add cash in multiples of this amount.</p>
                    <TextField
                        type="number"
                        size="small"
                        value={settings.cashTopupMultiple ?? ''}
                        onChange={(e) => handleChange('cashTopupMultiple', Number(e.target.value))}
                        sx={{ width: 200 }}
                    />
                </div>

                <button
                    onClick={saveHandler}
                    disabled={saving}
                    className="bg-primary-orange self-start px-6 py-2.5 text-white font-medium rounded shadow hover:shadow-lg disabled:opacity-60"
                >
                    {saving ? "Saving..." : "Save Changes"}
                </button>
            </div>
        </>
    );
};

export default WalletSettings;